import axios from 'axios';

// Use relative URL when in production (same server), absolute URL in development
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string means same origin
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001');
const API_KEY = process.env.REACT_APP_API_KEY || '';

// Debug logging
console.log('Backend API Service - Base URL:', API_BASE_URL);
console.log('Environment API URL:', process.env.REACT_APP_API_URL);

interface BackendAnalyticsData {
  website_id?: string;
  url_id?: string;
  check_date?: string;
  date?: string;
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
  pageviews?: number;
  unique_visitors?: number;
  bounce_rate?: number;
  avg_time_on_page?: number;
}

interface SearchConsoleData {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

class BackendApiService {
  public axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 120000, // Increased to 120 seconds for AI and PAA operations
    headers: {
      'X-API-Key': API_KEY,
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`
    }
  });

  async getAnalytics(websiteId: string, fromDate?: string, toDate?: string): Promise<BackendAnalyticsData[]> {
    try {
      const params: any = {
        website_id: websiteId,
        limit: 1000
      };

      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await this.axiosInstance.get('/api/analytics', { params });
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching analytics from backend:', error);
      return [];
    }
  }

  async getSearchConsoleData(websiteId: string, days: number = 30): Promise<SearchConsoleData[]> {
    try {
      const response = await this.axiosInstance.get('/api/search-console/overview', {
        params: {
          website_id: websiteId,
          days
        }
      });
      
      if (response.data && response.data.data && response.data.data.daily_data) {
        return response.data.data.daily_data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching search console data:', error);
      return [];
    }
  }

  async getWebsites(): Promise<any[]> {
    try {
      const response = await this.axiosInstance.get('/api/websites');
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching websites from backend:', error);
      return [];
    }
  }

  async getDashboardSummary(websiteId: string, days: number = 30): Promise<any> {
    try {
      const response = await this.axiosInstance.get('/api/dashboard/summary', {
        params: {
          website_id: websiteId,
          days
        }
      });
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data || {};
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      return {};
    }
  }

  async getAnalyticsGraph(websiteId: string, limit: number = 50, fromDate?: string, toDate?: string): Promise<any> {
    try {
      const params: any = {
        website_id: websiteId,
        limit
      };

      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await this.axiosInstance.get('/api/analytics/graph', { params });
      
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return response.data || {};
    } catch (error) {
      console.error('Error fetching analytics graph:', error);
      return {};
    }
  }

  async post<T = any>(endpoint: string, data: any, customTimeout?: number): Promise<T> {
    try {
      console.log('Making POST request to:', this.axiosInstance.defaults.baseURL + endpoint);
      console.log('Request data:', data);
      const config = customTimeout ? { timeout: customTimeout } : {};
      const response = await this.axiosInstance.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      console.error('Error making POST request:', error);
      console.error('Failed URL:', this.axiosInstance.defaults.baseURL + endpoint);
      throw error;
    }
  }

  async get<T = any>(endpoint: string, params?: any): Promise<T> {
    try {
      console.log('Making GET request to:', this.axiosInstance.defaults.baseURL + endpoint);
      const response = await this.axiosInstance.get(endpoint, { params });
      return response.data;
    } catch (error) {
      console.error('Error making GET request:', error);
      console.error('Failed URL:', this.axiosInstance.defaults.baseURL + endpoint);
      throw error;
    }
  }

  async put<T = any>(endpoint: string, data: any): Promise<T> {
    try {
      console.log('Making PUT request to:', this.axiosInstance.defaults.baseURL + endpoint);
      console.log('Request data:', data);
      const response = await this.axiosInstance.put(endpoint, data);
      return response.data;
    } catch (error) {
      console.error('Error making PUT request:', error);
      console.error('Failed URL:', this.axiosInstance.defaults.baseURL + endpoint);
      throw error;
    }
  }

  async getKeywordVolume(keyword: string, country: string = 'UK'): Promise<any> {
    try {
      const response = await this.axiosInstance.post('/api/keywords/metrics', { 
        keywords: [keyword],
        country: country,
        language: 'en'
      });
      
      if (response.data && response.data.metrics && response.data.metrics.length > 0) {
        return {
          keyword: keyword,
          search_volume: response.data.metrics[0].avgMonthlySearches || 0
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching keyword volume:', error);
      return null;
    }
  }

  async checkKeywordRankings(keyword: string, country: string = 'UK'): Promise<any> {
    try {
      const response = await this.axiosInstance.post('/api/serphouse/search', { 
        keyword: keyword,
        country: country,
        device: 'desktop',
        num_results: 20
      });
      
      if (response.data && response.data.success && response.data.data) {
        return {
          success: true,
          rankings: response.data.data.organic_results || []
        };
      }
      return null;
    } catch (error) {
      console.error('Error checking keyword rankings:', error);
      return null;
    }
  }
}

export const backendApiService = new BackendApiService();